// Device profiles for the "Best for your device" badge. Each profile maps
// a real-world player to the ideal torrent shape, so we can score every
// row in a torrent list and recommend the single best match.
//
// Scoring is a simple weighted sum:
//   - hard requirements ("max" / "must") that fail → row is disqualified
//   - soft preferences ("prefers") → add points if the row matches
// Whoever ends up with the highest score wins. Ties broken by seeders.

import {
  Tv as TvIcon,
  Smartphone,
  Monitor,
  HardDrive,
  Apple,
  Cast,
  Gamepad2,
  Sparkles,
} from "lucide-react";

export const DEVICES = [
  {
    id: "auto",
    label: "Auto (no recommendation)",
    short: "Auto",
    icon: Sparkles,
    description: "Don't single out any torrent. I'll choose myself.",
  },
  {
    id: "appletv-4k",
    label: "Apple TV 4K",
    short: "Apple TV 4K",
    icon: Apple,
    description: "Dolby Vision, Atmos, HEVC. The flagship 4K box.",
    prefers: {
      resolution: ["4K", "1080p"],
      codec: ["HEVC", "AV1"],
      hdr: ["DV", "HDR10+", "HDR10", "HDR"],
      audio: ["Atmos", "TrueHD", "DTS-HD"],
      release_source: ["REMUX", "BluRay", "WEB-DL"],
    },
    weights: { resolution: 30, codec: 20, hdr: 25, audio: 20, release_source: 10 },
  },
  {
    id: "appletv-hd",
    label: "Apple TV HD",
    short: "Apple TV HD",
    icon: Apple,
    description: "1080p only — older Apple TV without 4K output.",
    prefers: {
      resolution: ["1080p", "720p"],
      codec: ["HEVC", "H.264"],
      audio: ["AAC", "DDP5.1"],
    },
    disqualify: { resolution: ["4K", "1440p"] },
    weights: { resolution: 30, codec: 20, audio: 15 },
  },
  {
    id: "firetv-4k",
    label: "Fire TV 4K / Cube",
    short: "Fire TV 4K",
    icon: TvIcon,
    description: "Amazon's flagship — 4K HDR Atmos.",
    prefers: {
      resolution: ["4K", "1080p"],
      codec: ["HEVC", "AV1"],
      hdr: ["DV", "HDR10+", "HDR10", "HDR"],
      audio: ["Atmos", "DDP5.1"],
      release_source: ["REMUX", "BluRay", "WEB-DL"],
    },
    weights: { resolution: 30, codec: 20, hdr: 25, audio: 15, release_source: 10 },
  },
  {
    id: "firetv-hd",
    label: "Fire TV Stick (HD)",
    short: "Fire TV HD",
    icon: TvIcon,
    description: "1080p Fire TV Stick. No 4K, no Atmos.",
    prefers: {
      resolution: ["1080p", "720p"],
      codec: ["H.264", "HEVC"],
      audio: ["AAC", "DD5.1"],
    },
    disqualify: { resolution: ["4K", "1440p"] },
    weights: { resolution: 30, codec: 20, audio: 15 },
  },
  {
    id: "chromecast-gtv",
    label: "Chromecast / Google TV 4K",
    short: "Google TV 4K",
    icon: Cast,
    description: "Google's 4K HDR streamer.",
    prefers: {
      resolution: ["4K", "1080p"],
      codec: ["HEVC", "AV1"],
      hdr: ["DV", "HDR10+", "HDR10", "HDR"],
      audio: ["Atmos", "DDP5.1"],
    },
    weights: { resolution: 30, codec: 20, hdr: 25, audio: 15 },
  },
  {
    id: "smarttv-4k",
    label: "Smart TV (LG / Samsung / Sony 4K)",
    short: "4K Smart TV",
    icon: TvIcon,
    description: "Modern 4K HDR Smart TV with built-in apps.",
    prefers: {
      resolution: ["4K", "1080p"],
      codec: ["HEVC", "H.264"],
      hdr: ["HDR10", "HDR10+", "DV", "HDR"],
      audio: ["DDP5.1", "AAC"],
    },
    weights: { resolution: 30, codec: 15, hdr: 20, audio: 10 },
  },
  {
    id: "smarttv-hd",
    label: "Older Smart TV (1080p)",
    short: "1080p Smart TV",
    icon: TvIcon,
    description: "Pre-4K Smart TV. Stick to H.264 + AAC for max compatibility.",
    prefers: {
      resolution: ["1080p", "720p"],
      codec: ["H.264"],
      audio: ["AAC"],
    },
    disqualify: { resolution: ["4K", "1440p"], codec: ["AV1"] },
    weights: { resolution: 30, codec: 25, audio: 15 },
  },
  {
    id: "iphone",
    label: "iPhone / iPad",
    short: "iOS",
    icon: Smartphone,
    description: "Smaller files, HEVC plays great on modern iPhones.",
    prefers: {
      resolution: ["1080p", "720p"],
      codec: ["HEVC", "H.264"],
      audio: ["AAC"],
    },
    disqualify: { resolution: ["4K", "1440p"], codec: ["AV1", "XviD"] },
    weights: { resolution: 30, codec: 25, audio: 10 },
  },
  {
    id: "android-phone",
    label: "Android phone",
    short: "Android phone",
    icon: Smartphone,
    description: "1080p H.264 plays everywhere, smaller files preferred.",
    prefers: {
      resolution: ["1080p", "720p"],
      codec: ["H.264", "HEVC"],
      audio: ["AAC"],
    },
    disqualify: { resolution: ["4K", "1440p"], codec: ["AV1", "XviD"] },
    weights: { resolution: 30, codec: 25, audio: 10 },
  },
  {
    id: "pc-browser",
    label: "Gaming PC / Browser",
    short: "PC",
    icon: Gamepad2,
    description: "Anything plays. Lean toward 4K HDR if you've got the rig.",
    prefers: {
      resolution: ["4K", "1080p"],
      codec: ["HEVC", "AV1", "H.264"],
      hdr: ["HDR10", "HDR10+", "DV", "HDR"],
      audio: ["Atmos", "DDP5.1", "AAC"],
    },
    weights: { resolution: 25, codec: 15, hdr: 20, audio: 15 },
  },
  {
    id: "plex-nas",
    label: "Plex / Jellyfin / NAS",
    short: "Plex / NAS",
    icon: HardDrive,
    description: "Storage king. Prefers REMUX, lossless audio, 4K HDR.",
    prefers: {
      resolution: ["4K", "1080p"],
      codec: ["HEVC", "AV1", "H.264"],
      hdr: ["DV", "HDR10+", "HDR10", "HDR"],
      audio: ["Atmos", "TrueHD", "DTS-HD", "DTS-X", "DTS"],
      release_source: ["REMUX", "BluRay"],
    },
    weights: { resolution: 25, codec: 10, hdr: 20, audio: 25, release_source: 25 },
  },
  {
    id: "monitor-only",
    label: "Computer monitor (no HDR)",
    short: "Monitor",
    icon: Monitor,
    description: "SDR display — HDR rips will look washed out.",
    prefers: {
      resolution: ["4K", "1080p"],
      codec: ["HEVC", "AV1", "H.264"],
      audio: ["AAC", "DDP5.1"],
    },
    disqualify: { hdr: ["DV", "HDR10+", "HDR10", "HDR"] },
    weights: { resolution: 25, codec: 15, audio: 10 },
  },
];

export const DEFAULT_DEVICE_ID = "auto";
const STORAGE_KEY = "torhub:device";

export function loadDeviceId() {
  try {
    return localStorage.getItem(STORAGE_KEY) || DEFAULT_DEVICE_ID;
  } catch {
    return DEFAULT_DEVICE_ID;
  }
}

export function saveDeviceId(id) {
  try {
    if (!id || id === DEFAULT_DEVICE_ID) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, id);
  } catch { /* no-op */ }
}

export function findDevice(id) {
  return DEVICES.find((d) => d.id === id) || DEVICES[0];
}

/** Score a single torrent row against a device profile.
 *  Returns:
 *    - score: positive int, higher = better fit
 *    - disqualified: true if the row violates a hard rule (e.g. 4K on an
 *      HD-only device). Disqualified rows can never win.
 *    - reasons: ordered list of "why this is the pick" strings, used in
 *      the tooltip.
 */
export function scoreRow(row, device) {
  if (!device || !device.prefers) return { score: 0, disqualified: false, reasons: [] };

  // Hard disqualifiers
  if (device.disqualify) {
    for (const [field, blocked] of Object.entries(device.disqualify)) {
      const v = row[field];
      if (!v) continue;
      if (Array.isArray(v)) {
        if (v.some((x) => blocked.includes(x))) return { score: 0, disqualified: true, reasons: [] };
      } else if (blocked.includes(v)) {
        return { score: 0, disqualified: true, reasons: [] };
      }
    }
  }

  let score = 0;
  const reasons = [];
  const weights = device.weights || {};
  for (const [field, ranked] of Object.entries(device.prefers)) {
    const v = row[field];
    if (!v) continue;
    const weight = weights[field] ?? 10;
    if (Array.isArray(v)) {
      // Multi-value (audio chips) — take the best-ranked entry
      let bestIdx = -1;
      for (const tag of v) {
        const idx = ranked.indexOf(tag);
        if (idx >= 0 && (bestIdx === -1 || idx < bestIdx)) bestIdx = idx;
      }
      if (bestIdx >= 0) {
        const points = Math.max(1, weight - bestIdx * 4);
        score += points;
        reasons.push(`${ranked[bestIdx]} ✓`);
      }
    } else {
      const idx = ranked.indexOf(v);
      if (idx >= 0) {
        const points = Math.max(1, weight - idx * 4);
        score += points;
        reasons.push(`${v} ✓`);
      }
    }
  }
  // Soft seeders bonus — 1 point per 100 seeders, capped at 10. Keeps high-
  // health torrents winning ties without overwhelming codec/resolution.
  score += Math.min(10, Math.floor((row.seeders || 0) / 100));
  return { score, disqualified: false, reasons };
}

/** Given a list of rows + device, return the row with the highest score
 *  (or null if no device is set / no row qualifies). */
export function pickBestRow(rows, device) {
  if (!device || device.id === "auto" || !rows?.length) return null;
  let best = null;
  let bestScore = 0;
  let bestReasons = [];
  for (const r of rows) {
    const { score, disqualified, reasons } = scoreRow(r, device);
    if (disqualified || score <= 0) continue;
    if (score > bestScore) {
      best = r;
      bestScore = score;
      bestReasons = reasons;
    }
  }
  if (!best) return null;
  return { row: best, score: bestScore, reasons: bestReasons };
}
